import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../../lib/api';
import {
  Mail, ArrowLeft, Search, Loader2, AlertTriangle, CheckCircle2,
  XCircle, Send, RefreshCw, ShieldAlert,
} from 'lucide-react';

const StatChip = ({ ok, okText, badText, testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-display font-semibold ${
      ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-300'
    }`}
  >
    {ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
    {ok ? okText : badText}
  </span>
);

export default function EmailDiagnostics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState('');

  const load = async (lookup) => {
    try {
      const res = await apiGet('/notifications/digest/preview-recipients', lookup ? { lookup } : undefined);
      setData(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load diagnostics');
    }
  };

  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  const runSearch = async () => {
    if (!query.trim()) { toast.error('Type a name or email to look up'); return; }
    setSearching(true);
    await load(query.trim());
    setSearching(false);
  };

  const reEnable = async (email, eventType) => {
    setBusy(`${email}:${eventType}`);
    try {
      await apiPost('/notifications/prefs/set', { email, event_type: eventType, enabled: true });
      toast.success(`Re-enabled ${eventType.replace(/_/g, ' ')} for ${email}`);
      await load(query.trim() || undefined);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update preference');
    }
    setBusy('');
  };

  const sendTest = async (email) => {
    setBusy(`${email}:test`);
    try {
      await apiPost('/settings/email/test', { to_email: email });
      toast.success(`Test email sent to ${email} — ask them to check inbox + spam`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test send failed');
    }
    setBusy('');
  };

  const triggerNow = async () => {
    setBusy('trigger');
    try {
      await apiPost('/notifications/trigger/daily-attendance-digest', {});
      toast.success('Daily attendance digest dispatched to all current recipients');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not trigger digest');
    }
    setBusy('');
  };

  if (loading) {
    return <div className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Loading diagnostics…</div>;
  }

  const lookup = data?.lookup || {};
  const lookupUsers = lookup.users || [];
  const lookupEmps = lookup.employees || [];
  const hasSearched = !!lookup.query;

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="email-diagnostics-page">
      <button onClick={() => navigate('/app/settings/email')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#3b4872]">
        <ArrowLeft size={15} /> Back to Email Configuration
      </button>

      <div>
        <div className="flex items-center gap-2">
          <Mail size={20} className="text-[#3b4872]" />
          <h1 className="text-xl font-display font-bold text-[#0F172A]">Daily Email Diagnostics</h1>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Figure out exactly why someone is (or isn&apos;t) receiving the daily digests — without sending real mail to everyone.
        </p>
      </div>

      {/* System status */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-display font-bold mb-3">System Status</div>
        <div className="flex flex-wrap gap-3">
          <StatChip ok={data?.smtp_configured} okText="SMTP configured" badText="SMTP NOT configured" testid="diag-smtp-status" />
          <StatChip ok={!data?.test_redirect_active} okText="Test mode OFF (real recipients)" badText={`TEST MODE ON → all mail to ${data?.test_redirect_to || 'test address'}`} testid="diag-testmode-status" />
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-display font-semibold bg-slate-100 text-slate-700 border border-slate-200" data-testid="diag-recipient-count">
            {data?.count ?? 0} digest recipients
          </span>
        </div>
        {data?.test_redirect_active && (
          <div className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-sm p-3">
            <ShieldAlert size={15} className="mt-0.5 shrink-0" />
            <span>
              <b>Test Mode is ON.</b> Every notification email is being redirected to <b>{data.test_redirect_to}</b> — real people receive nothing.
              Turn it off in <button className="underline font-semibold" onClick={() => navigate('/app/settings/email')}>Email Configuration</button>.
            </span>
          </div>
        )}
        {data?.orphan_count > 0 && (
          <div className="mt-3 text-xs text-slate-600">
            <b>{data.orphan_count}</b> orphaned notification-preference record(s) (email no longer matches any active user/employee) — usually a leftover after a domain/email change.
          </div>
        )}
        <div className="mt-4">
          <button
            onClick={triggerNow}
            disabled={busy === 'trigger'}
            data-testid="diag-trigger-now"
            className="inline-flex items-center gap-2 px-3 py-2 bg-[#3b4872] hover:bg-[#2c365a] text-white text-xs font-display font-bold rounded-sm disabled:opacity-50"
          >
            {busy === 'trigger' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Send today&apos;s digest now (to all recipients)
          </button>
        </div>
      </div>

      {/* Person lookup */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-display font-bold mb-2">Find a person</div>
        <p className="text-xs text-slate-500 mb-3">Search by name or email (e.g. &quot;Mayur&quot;) to see their email on file, whether they are a current recipient, and their opt-in status.</p>
        <div className="flex gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runSearch()}
            placeholder="Name or email…"
            className="flex-1 px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#3b4872]"
            data-testid="diag-search-input"
          />
          <button
            onClick={runSearch}
            disabled={searching}
            data-testid="diag-search-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#3b4872] hover:bg-[#2c365a] text-white text-sm font-display font-bold rounded-sm disabled:opacity-50"
          >
            {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            Search
          </button>
        </div>

        {hasSearched && lookupUsers.length === 0 && lookupEmps.length === 0 && (
          <div className="mt-4 text-sm text-slate-500">No users or employees match &quot;{lookup.query}&quot;.</div>
        )}

        {lookupUsers.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold mb-2">Login / User records</div>
            <div className="space-y-3">
              {lookupUsers.map((u, i) => (
                <div key={u.id || u.email || i} className="border border-slate-200 rounded-sm p-3" data-testid={`diag-user-${i}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="font-display font-bold text-sm">{u.name} <span className="text-slate-400 font-normal">· {u.department || '—'} {u.role ? `· ${u.role}` : ''}</span></div>
                      <div className="text-xs text-slate-600 font-mono">{u.email || <span className="text-red-500">no email on file</span>}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {u.is_recipient
                        ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold"><CheckCircle2 size={13} /> In recipient list</span>
                        : <span className="inline-flex items-center gap-1 text-xs text-amber-700 font-semibold"><XCircle size={13} /> NOT a recipient</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-sm border ${u.attendance_opted_in ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                      Attendance digest: {u.attendance_opted_in ? 'opted-in' : 'OPTED-OUT'}
                    </span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-sm border ${u.task_opted_in ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                      Task digest: {u.task_opted_in ? 'opted-in' : 'OPTED-OUT'}
                    </span>
                    {u.is_active === false && <span className="text-[11px] px-2 py-0.5 rounded-sm border bg-red-50 text-red-700 border-red-200">INACTIVE</span>}
                  </div>
                  {u.email && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {!u.attendance_opted_in && (
                        <button onClick={() => reEnable(u.email, 'daily_attendance_digest')} disabled={busy === `${u.email}:daily_attendance_digest`} data-testid={`diag-enable-att-${i}`} className="text-[11px] px-2.5 py-1.5 rounded-sm border border-[#3b4872] text-[#3b4872] hover:bg-[#3b4872] hover:text-white font-display font-semibold disabled:opacity-50">
                          Re-enable attendance digest
                        </button>
                      )}
                      {!u.task_opted_in && (
                        <button onClick={() => reEnable(u.email, 'daily_task_digest')} disabled={busy === `${u.email}:daily_task_digest`} data-testid={`diag-enable-task-${i}`} className="text-[11px] px-2.5 py-1.5 rounded-sm border border-[#3b4872] text-[#3b4872] hover:bg-[#3b4872] hover:text-white font-display font-semibold disabled:opacity-50">
                          Re-enable task digest
                        </button>
                      )}
                      <button onClick={() => sendTest(u.email)} disabled={busy === `${u.email}:test`} data-testid={`diag-test-${i}`} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 font-display font-semibold disabled:opacity-50">
                        <Send size={12} /> Send test email
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {lookupEmps.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold mb-2">Employee (HR) records</div>
            <div className="space-y-2">
              {lookupEmps.map((e, i) => (
                <div key={e.id || i} className="border border-slate-200 rounded-sm p-3 text-xs" data-testid={`diag-emp-${i}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <span className="font-display font-bold text-sm">{e.name}</span>
                      <span className="text-slate-400"> · {e.emp_id} · {e.department || '—'} · {e.designation || '—'}</span>
                    </div>
                    {e.is_recipient
                      ? <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 size={12} /> recipient</span>
                      : <span className="inline-flex items-center gap-1 text-amber-700 font-semibold"><XCircle size={12} /> not a recipient</span>}
                  </div>
                  <div className="mt-1 font-mono text-slate-600">
                    email: {e.email || <span className="text-red-500">none</span>}
                    {e.contact_email ? ` · contact: ${e.contact_email}` : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {hasSearched && (lookupUsers.length > 0 || lookupEmps.length > 0) && (
          <div className="mt-4 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-3">
            <b>How to read this:</b> If the email on file is wrong (e.g. an off-domain address), fix it in HR → Employee Master <i>and</i> Settings → User Management. If they show <b>OPTED-OUT</b>, use the re-enable button. If everything looks right but they still report nothing, use <b>Send test email</b> — if even that doesn&apos;t arrive, the problem is their mailbox/spam filter, not the ERP.
          </div>
        )}
      </div>
    </div>
  );
}
